// 法律文本页的骨架，隐私政策和服务条款共用。
//
// 正文一律从 JSON 数据来（src/legal/*.json），不写死在 JSX 里：
// 那两份条款是从律师给的 docx 里用脚本抽出来的，六十多万字节的法律文本
// 手抄一定会抄错，而抄错一个「不」字就是另一层意思。要改条款改 JSON。
//
// 「待补充」这三个字是有意留在正文里的 —— ABN、隐私联络人、邮箱这些只有
// 公司能提供。留着比编一个占位邮箱强：编的那个万一被人当真发了邮件，
// 信就永远没人收。页面上会把这几处标出来，方便一眼找到还差什么。

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './legal.css';

// 站点用的是 HashRouter，所以**目录不能用 `href="#sec-3"`**：
// 那种锚点会把整个 hash 替换掉，`#/privacyPolicy` 变成 `#sec-3`，
// 路由匹配不到就落到通用空白页 —— 点一下整页变白。
// 2026-09-18 线上真出过，用户第一时间就点到了。
// 所以目录用按钮 + scrollIntoView，不碰地址栏。
function scrollToSec(n) {
  const el = document.getElementById(`sec-${n}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// App 的 webview 打开这两页时会把自己当前的语言带过来（`?lang=zh`）。
// 带了就听它的 —— 用户在 App 里选的是中文，点开条款却是英文，会以为点错了。
// 注意站点是 HashRouter，query 在 hash 里（`#/privacyPolicy?lang=zh`），
// `window.location.search` 是空的，取错地方就永远读不到。
function langFromUrl() {
  try {
    const q = (window.location.hash || '').split('?')[1] || '';
    const v = new URLSearchParams(q).get('lang');
    return v === 'en' || v === 'zh' ? v : '';
  } catch (e) { return ''; }
}

const T = {
  zh: {
    brand: '券宝宝 · Grow Rich Marketing Pty Ltd',
    toc: '目录',
    lang: '语言',
    updated: '最后更新',
    pending: '待补充',
    law: '适用法律',
    lawVal: '澳大利亚联邦 · 维多利亚州',
    footNote: '本页内容如与您接受合作时的具体活动条件冲突，具体活动条件在该次合作范围内优先，但不排除依法不可排除的权利。',
    other: (name) => `另见：${name}`,
  },
  en: {
    brand: 'Couponer · Grow Rich Marketing Pty Ltd',
    toc: 'Contents',
    lang: 'Language',
    updated: 'Last updated',
    pending: 'To be confirmed',
    law: 'Governing law',
    lawVal: 'Commonwealth of Australia · Victoria',
    footNote:
      'Where this page conflicts with the specific campaign conditions you accepted, those conditions prevail for that collaboration only, without excluding rights that cannot be excluded by law.',
    other: (name) => `See also: ${name}`,
  },
};

// 正文里凡是出现这几个词，就在后面挂一个「待补充」标记，
// 免得定稿时漏掉某一处。中英各一套。
const PENDING_RE = /待补充|待确认|to be confirmed/i;

function Item({ n, t }) {
  const flagged = PENDING_RE.test(t);
  return (
    <div className="lg-item">
      <div className="n">{n}</div>
      <div className="t">
        {t}
        {flagged && <span className="lg-todo">待补充 · TBC</span>}
      </div>
    </div>
  );
}

export default function LegalDoc({
  docKey,          // localStorage 里记语言用，两页各记各的
  titleOf,         // (lang) => 标题
  ledeOf,          // (lang) => 一句话说明
  sectionsOf,      // (lang) => [{ n, title, items, moved }]
  updated,
  tabs,            // 可选：[{ key, label: {zh,en} }]，服务条款用
  activeTab,
  onTab,
  footLinks,       // 可选：[{ href, label: {zh,en} }]
}) {
  // 默认英文。这两份是拿给澳洲这边看的合规文件 ——
  // 审的人、引用的人（App Store 审核、商家的律师、Google/小红书那边）都读英文，
  // 中文是给自己人看的翻译。看中文的人点一下就切，而且会被记住；
  // 让默认落在英文上，是让第一眼进来的人不用先找语言开关。
  const [lang, setLang] = useState('en');
  const [cur, setCur] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    // 链接里指定的语言优先于上次记住的：从 App 点进来的那一下，
    // 该跟着 App 走，而不是跟着这个 webview 上回的选择走。
    // 但**不写回 localStorage** —— 用户在这页手动切过的选择不该被一次跳转覆盖。
    const fromUrl = langFromUrl();
    if (fromUrl) { setLang(fromUrl); return; }
    try {
      const saved = localStorage.getItem(`lg-lang-${docKey}`);
      if (saved === 'en' || saved === 'zh') setLang(saved);
    } catch (e) { /* 隐私模式下读不到就用默认，不影响阅读 */ }
  }, [docKey]);

  const pick = useCallback((l) => {
    setLang(l);
    try { localStorage.setItem(`lg-lang-${docKey}`, l); } catch (e) { /* 同上 */ }
  }, [docKey]);

  const t = T[lang];
  const sections = sectionsOf(lang) || [];

  // 目录高亮。切文档或切语言之后要重新观察 —— 不重建的话，
  // 观察的还是上一份文档已经被卸载的节点，目录就再也不动了
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const nodes = Array.from(bodyRef.current?.querySelectorAll('.lg-sec') || []);
    if (!nodes.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setCur(e.target.id); });
    }, { rootMargin: '-8% 0px -78% 0px' });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [lang, activeTab, sections.length]);

  return (
    <div className="lg-page">
      <div className="lg-wrap">
        <header className="lg-top">
          <p className="lg-brandline">{t.brand}</p>
          <h1>{titleOf(lang)}</h1>
          <p className="lg-lede">{ledeOf(lang)}</p>
          {/* 只写「最后更新」，不写「生效日期」，也不列版本历史。
              版本号和生效日期会让人追问「那上一版是什么」—— 而上一版是一份
              误挂上来的第三方模板，把注意力引到那儿对谁都没好处。
              澳洲 APP 1 要求的是「有一份清晰、最新、免费可取的隐私政策」，
              没有要求标版本或生效日。一个「最后更新」既是事实，也够用。 */}
          <div className="lg-meta">
            <span><b>{t.updated}</b> {updated}</span>
            <span><b>{t.law}</b> {t.lawVal}</span>
          </div>
          <div className="lg-lang" role="group" aria-label={t.lang}>
            <button type="button" aria-pressed={lang === 'zh'} onClick={() => pick('zh')}>中文</button>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => pick('en')}>English</button>
          </div>
        </header>

        {/* 窄屏：目录收进一个按钮里。十几条条款标题直接铺在正文上面，
            手机上要划过整屏才看得到第一段 —— 那等于把目录做成了路障。
            宽屏不显示这个按钮，目录常驻左侧。 */}
        <button
          type="button"
          className="lg-menubtn"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="lg-menubtn__ico" aria-hidden="true"><i /><i /><i /></span>
          {menuOpen ? (lang === 'zh' ? '收起目录' : 'Hide contents') : t.toc}
        </button>

        <div className="lg-cols">
          <nav
            className={`lg-toc${menuOpen ? ' lg-toc--open' : ''}`}
            aria-label={t.toc}
          >
            {tabs && (
              <>
                <div className="grp">{lang === 'zh' ? '文件' : 'Documents'}</div>
                <div className="lg-tabs lg-tabs--stack">
                  {tabs.map((tb) => (
                    <button
                      key={tb.key}
                      type="button"
                      aria-pressed={activeTab === tb.key}
                      onClick={() => { onTab(tb.key); setMenuOpen(false); }}
                    >
                      {tb.label[lang]}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="grp">{t.toc}</div>
            <ol>
              {sections.map((s) => (
                <li key={s.n}>
                  <button
                    type="button"
                    className={cur === `sec-${s.n}` ? 'on' : ''}
                    onClick={() => { scrollToSec(s.n); setMenuOpen(false); }}
                  >
                    <span className="num">{s.n}</span>
                    <span>{s.title}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          <main className="lg-body" ref={bodyRef}>
            {sections.map((s) => (
              <section className="lg-sec" id={`sec-${s.n}`} key={s.n}>
                <h2><span className="num">{s.n}</span><span>{s.title}</span></h2>
                {s.moved && (
                  <div className="lg-moved">
                    {s.items[0]?.t}
                    {' '}
                    <a href="#/privacyPolicy">
                      {lang === 'zh' ? '打开隐私政策' : 'Open the Privacy Policy'}
                    </a>
                  </div>
                )}
                {(s.moved ? s.items.slice(1) : s.items).map((it, i) => (
                  <Item key={it.n || `x${i}`} n={it.n} t={it.t} />
                ))}
              </section>
            ))}

            <footer className="lg-foot">
              <p>{t.footNote}</p>
              {footLinks && footLinks.map((l) => (
                <p key={l.href}><a href={l.href}>{t.other(l.label[lang])}</a></p>
              ))}
              <p>© {new Date().getFullYear()} Grow Rich Marketing Pty Ltd</p>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
